import { useEffect, useMemo } from 'react';
import * as THREE from 'three';
import { useThree } from '@react-three/fiber';
import { useSketchStore } from '../../state/useSketchStore';
import { sunDirection } from '../../core/sketch/sun';

/**
 * Sketch sun — the Shadows panel's viewport side.
 *
 * A single directional sun (position from day/hour solar approximation) with
 * a shadow-catcher ground plane (ShadowMaterial: only shadows render).
 * Faces opt in per-mesh via the solid layer (castShadow/receiveShadow follow
 * shadows.enabled).
 *
 * The ambient/hemisphere FILL lives in SketchSceneContent and is mounted
 * ALWAYS (also with the panel off) — this component only adds the sun and
 * its shadows, so toggling the panel never regrades the base lighting.
 * Surfaces pointing away from the sun keep a readable dark tone from the
 * fill — never pure black under ACES tone mapping.
 */

const SUN_DISTANCE = 120;
const SHADOW_EXTENT = 60;

export function SketchSunLight() {
    const shadows = useSketchStore((s) => s.shadows);
    const gl = useThree((s) => s.gl);

    const sun = useMemo(
        () => sunDirection(shadows.day, shadows.hour),
        [shadows.day, shadows.hour]
    );
    // Keep a working shadow even at the horizon: clamp the sun's altitude so
    // the shadow frustum never degenerates at dawn/dusk extremes.
    const position = useMemo(() => {
        const dir = sun.direction.clone();
        dir.z = Math.max(dir.z, 0.12);
        return dir.normalize().multiplyScalar(SUN_DISTANCE);
    }, [sun]);

    // The Canvas `shadows` flag only applies on mount; forcing the renderer
    // state here makes runtime toggles reliable (demand-render loop included).
    useEffect(() => {
        gl.shadowMap.enabled = true;
        gl.shadowMap.type = THREE.PCFSoftShadowMap;
        gl.shadowMap.needsUpdate = true;
        return () => {
            gl.shadowMap.enabled = false;
            gl.shadowMap.needsUpdate = true;
        };
    }, [gl]);

    return (
        <group>
            <directionalLight
                position={position}
                intensity={0.4 + shadows.light * 0.9}
                castShadow
                shadow-mapSize-width={2048}
                shadow-mapSize-height={2048}
                shadow-camera-left={-SHADOW_EXTENT}
                shadow-camera-right={SHADOW_EXTENT}
                shadow-camera-top={SHADOW_EXTENT}
                shadow-camera-bottom={-SHADOW_EXTENT}
                shadow-camera-near={1}
                shadow-camera-far={SUN_DISTANCE * 3}
                shadow-bias={-0.0002}
            />
            {/* Shadow catcher: invisible except where shadows land. No
                rotation — the plane is born on the XY ground (Z-up world).
                Slightly below the ground so the grid stays crisp above it. */}
            <mesh
                position={[0, 0, -0.02]}
                receiveShadow
            >
                <planeGeometry args={[400, 400]} />
                <shadowMaterial opacity={shadows.dark * 0.6} />
            </mesh>
        </group>
    );
}
